import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from '@reduxjs/toolkit';

import apiReducer from './slices/directusSlice';
import navigationReducer from './slices/navigationSlice';
import authReducer from './slices/authSlice';
import visitsReducer from './slices/visitsSlice';
import customersReducer from './slices/customersSlice';
import evidenceReducer from './slices/evidenceSlice';
import onboardingReducer from './slices/onboardingSlice';
import interactionsReducer from './slices/interactionsSlice';
import userStateReducer from './slices/userStateSlice';
import transcriptionReducer from './slices/transcriptionSlice';
import productsReducer from './slices/productsSlice';
import productPromotionsReducer from './slices/productPromotionsSlice';
import { env } from '../lib/env';
import { persistTransforms } from './persistence/transforms';
import { enhancedNavigationMiddleware } from './middleware/navigationMiddleware';

// Combine reducers
const rootReducer = combineReducers({
  api: apiReducer,
  navigation: navigationReducer,
  auth: authReducer,
  visits: visitsReducer,
  customers: customersReducer,
  evidence: evidenceReducer,
  onboarding: onboardingReducer,
  interactions: interactionsReducer,
  userState: userStateReducer,
  transcription: transcriptionReducer,
  products: productsReducer,
  productPromotions: productPromotionsReducer,
});

// Configure persistence with enhanced state recovery
const persistConfig = {
  key: 'root',
  storage,
  version: 1,
  // Expanded whitelist for comprehensive state recovery
  whitelist: [
    'onboarding',    // User onboarding state
    'userState',     // Session data, forms, drafts, accessibility
    'navigation',    // Navigation context and screen history
    'customers',     // Selected customer and recent selections
    'visits',        // Active visit and visit data (sanitized)
    'evidence',      // Evidence drafts and metadata (no blobs)
  ],
  transforms: persistTransforms,
  // Remove auth from whitelist to prevent stale token caching issues
  // Auth state will be restored from DirectusAuthManager after server validation
  // Transcription and interactions are excluded to prevent storage bloat
  debug: env.DEV, // Enable debug logging in development
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredActionsPaths: [
          'payload.timestamp',
          'meta.arg',
          'meta.baseQueryMeta',
          'payload.evidence.blob', // Ignore blob in evidence actions
          'payload.blob' // Ignore blob in direct payload
        ],
        ignoredPaths: [
          'api.dashboardData.timestamp',
          'api.employeeStats.timestamp',
          'evidence.evidence', // Ignore all evidence state (contains blobs)
          'transcription.transcriptions', // Ignore transcription state (contains audio blobs)
          'transcription.currentTranscription', // Ignore current transcription (contains audio blob)
        ],
      },
    }).concat(enhancedNavigationMiddleware), // Add navigation middleware
  devTools: env.DEV,
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Create persistor
export const persistor = persistStore(store);

export default store;